import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import type { AstrologerClient, AstrologerProfile } from '../../../types/professional';

type ClientWithAstrologer = AstrologerClient & { astrologer_name?: string };

export function AdminAstrologerClientsPage() {
  const [clients, setClients]   = useState<ClientWithAstrologer[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterAp, setFilterAp] = useState('');
  const [astrologers, setAstrologers] = useState<Pick<AstrologerProfile, 'id' | 'professional_name'>[]>([]);

  useEffect(() => {
    Promise.all([
      supabase.from('astrologer_clients').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('astrologer_profiles').select('id, professional_name').order('professional_name'),
    ]).then(([{ data: c }, { data: a }]) => {
      const apMap: Record<string, string> = {};
      for (const ap of (a ?? [])) apMap[ap.id] = ap.professional_name;
      setAstrologers((a ?? []) as Pick<AstrologerProfile, 'id' | 'professional_name'>[]);
      setClients(((c ?? []) as AstrologerClient[]).map(cl => ({ ...cl, astrologer_name: apMap[cl.astrologer_id] ?? cl.astrologer_id })));
      setLoading(false);
    });
  }, []);

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.email ?? '').toLowerCase().includes(q);
    const matchAp = !filterAp || c.astrologer_id === filterAp;
    return matchSearch && matchAp;
  });

  const cardStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1rem 1.25rem' };

  function downloadCSV() {
    const headers = ['Nome', 'Email', 'Telefone', 'Data Nascimento', 'Local Nascimento', 'Signo Solar', 'Astrólogo', 'Ativo', 'Cadastro'];
    const rows = filtered.map(c => [
      c.name,
      c.email ?? '',
      c.phone ?? '',
      c.birth_date ? new Date(c.birth_date + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      c.birth_city ?? '',
      c.natal_chart_json?.sun_sign ?? '',
      c.astrologer_name ?? '',
      c.is_active ? 'Sim' : 'Não',
      new Date(c.created_at).toLocaleDateString('pt-BR'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `clientes_astrologos_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: 'clamp(1.1rem,3vw,1.4rem)', color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-display)' }}>
          Todos os Clientes
        </h1>
        <button
          className="btn btn-secondary btn-sm"
          onClick={downloadCSV}
          disabled={filtered.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
        >
          <Download size={13} /> Baixar CSV ({filtered.length})
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: '200px' }}
          placeholder="Buscar por nome ou e-mail…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="form-input" style={{ width: 'auto' }} value={filterAp} onChange={e => setFilterAp(e.target.value)}>
          <option value="">Todos os astrólogos</option>
          {astrologers.map(a => <option key={a.id} value={a.id}>{a.professional_name}</option>)}
        </select>
        <span style={{ alignSelf: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{filtered.length} cliente{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: '2rem' }}>Carregando…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Nenhum resultado.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {filtered.map(c => (
            <div key={c.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{c.name}</p>
                <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  {c.astrologer_name} · {new Date(c.birth_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                  {c.birth_city && ` · ${c.birth_city}`}
                  {c.natal_chart_json?.sun_sign && ` · ☀ ${c.natal_chart_json.sun_sign}`}
                </p>
              </div>
              {!c.is_active && <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>inativo</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
